/**
 * Feature E03-F04 / F05 / F07 — typed web api-client + TanStack mutations for the
 * Stage-3 document generation, editor and internal-notification surfaces.
 *
 * Thin wrappers over the shared `apiClient` (auto Bearer token, normalized
 * `ApiResult`, never throws) speaking the EXACT `@eer/shared` contracts the
 * Fastify routes validate — the cross-package seam. `unwrap` is the single
 * throw-at-the-Query-boundary helper, so every mutation's `error` is a typed
 * `ApiResultError`.
 *
 * Endpoints (packages/api/src/routes/{doc-generation,doc-edit-regen,internal-notification,email-send}.ts):
 *   POST /projects/:id/ai/generate_documents        → generate_documents (Gen)   [F04-T07]
 *   POST /projects/:id/ai/generate_kickoff_sheet     → generate_kickoff_sheet (KS)
 *   POST /projects/:id/ai/generate_followup_email    → generate_followup_email (FU)
 *   POST /projects/:id/artifacts/:type/regenerate    → regenerate_document        [F05]
 *   PUT  /projects/:id/artifacts/:type               → save_stage4_artifact       [F05]
 *   POST /projects/:id/artifacts/:type/approve       → approve_artifact           [F05]
 *   POST /projects/:id/confirm-package               → confirm_package
 *   GET  /projects/:id/ai/internal_notification_defaults → notif defaults         [F07]
 *   POST /projects/:id/email/send                    → S5 universal send          [F07]
 *
 * The actor (`created_by`) is NEVER a body field — it is taken from the session
 * by the api (IDOR rule); the SPA only ever sends `generation_notes` /
 * `instruction` / the edited content / the composed email.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  GenerateDocumentsResponse,
  GenerateKickoffSheetResponse,
  GenerateFollowupEmailResponse,
  RegenerateDocumentResponse,
  SaveArtifactResponse,
  ApproveArtifactResponse,
  ConfirmPackageResponse,
  EditableDocType,
  RegeneratableDocType,
  InternalNotificationDefaultsResponse,
  SendStage5Request,
  SendStage5Response,
  PackageLinksResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { qk } from '../api/queryKeys.js';

// ---------------------------------------------------------------------------
// fetchers (each returns the unwrapped typed body, or throws ApiResultError)
// ---------------------------------------------------------------------------

/** POST .../ai/generate_documents — the Gen batch (JD / InMails / IQ / SS). */
export async function generateDocumentsApi(
  projectId: number,
  generationNotes?: string,
): Promise<GenerateDocumentsResponse> {
  return unwrap(
    await apiClient.post<GenerateDocumentsResponse>(
      `/projects/${projectId}/ai/generate_documents`,
      { generation_notes: generationNotes },
      { command: 'generate_documents' },
    ),
  );
}

/** POST .../ai/generate_kickoff_sheet — the KS special item. */
export async function generateKickoffSheetApi(
  projectId: number,
  generationNotes?: string,
): Promise<GenerateKickoffSheetResponse> {
  return unwrap(
    await apiClient.post<GenerateKickoffSheetResponse>(
      `/projects/${projectId}/ai/generate_kickoff_sheet`,
      { generation_notes: generationNotes },
      { command: 'generate_kickoff_sheet' },
    ),
  );
}

/** POST .../ai/generate_followup_email — the FU special item (fast-track). */
export async function generateFollowupEmailApi(
  projectId: number,
  generationNotes?: string,
): Promise<GenerateFollowupEmailResponse> {
  return unwrap(
    await apiClient.post<GenerateFollowupEmailResponse>(
      `/projects/${projectId}/ai/generate_followup_email`,
      { generation_notes: generationNotes },
      { command: 'generate_followup_email' },
    ),
  );
}

/** POST .../artifacts/:type/regenerate — regenerate one core doc with instruction. */
export async function regenerateDocumentApi(
  projectId: number,
  docType: RegeneratableDocType,
  instruction: string,
  generationNotes: string,
): Promise<RegenerateDocumentResponse> {
  return unwrap(
    await apiClient.post<RegenerateDocumentResponse>(
      `/projects/${projectId}/artifacts/${docType}/regenerate`,
      { instruction, generation_notes: generationNotes },
      { command: 'regenerate_document' },
    ),
  );
}

/** PUT .../artifacts/:type — save an edited artifact (status='edited'). */
export async function saveArtifactApi(
  projectId: number,
  docType: EditableDocType,
  content: Record<string, unknown>,
): Promise<SaveArtifactResponse> {
  return unwrap(
    await apiClient.put<SaveArtifactResponse>(
      `/projects/${projectId}/artifacts/${docType}`,
      { content },
      { command: 'save_stage4_artifact' },
    ),
  );
}

/** POST .../artifacts/:type/approve — approve an artifact (status='approved'). */
export async function approveArtifactApi(
  projectId: number,
  docType: EditableDocType,
): Promise<ApproveArtifactResponse> {
  return unwrap(
    await apiClient.post<ApproveArtifactResponse>(
      `/projects/${projectId}/artifacts/${docType}/approve`,
      {},
      { command: 'approve_artifact' },
    ),
  );
}

/** POST .../confirm-package — move the project to Final once all 4 are approved. */
export async function confirmPackageApi(projectId: number): Promise<ConfirmPackageResponse> {
  return unwrap(
    await apiClient.post<ConfirmPackageResponse>(
      `/projects/${projectId}/confirm-package`,
      {},
      { command: 'confirm_package' },
    ),
  );
}

/** GET .../ai/internal_notification_defaults — recipients + both templates. */
export async function internalNotificationDefaultsApi(
  projectId: number,
): Promise<InternalNotificationDefaultsResponse> {
  return unwrap(
    await apiClient.get<InternalNotificationDefaultsResponse>(
      `/projects/${projectId}/ai/internal_notification_defaults`,
      { command: 'get_notification_defaults' },
    ),
  );
}

/** POST .../email/send — universal Stage-5 send (used for internal_notification). */
export async function sendStage5Api(
  projectId: number,
  body: SendStage5Request,
): Promise<SendStage5Response> {
  return unwrap(
    await apiClient.post<SendStage5Response>(`/projects/${projectId}/email/send`, body, {
      command: 'send_stage5_email',
    }),
  );
}

/**
 * POST .../package/links — render each EXISTING generation doc to a `.doc`, upload
 * it to R2 and return one clickable link per doc (PK-QA-36, "Send Final Package").
 * Bare-array response (may be `[]` / shorter than 6); a 500 `R2_NOT_CONFIGURED`
 * surfaces as a typed `ApiResultError` the caller toasts (never a silent failure).
 */
export async function packageLinksApi(projectId: number): Promise<PackageLinksResponse> {
  return unwrap(
    await apiClient.post<PackageLinksResponse>(
      `/projects/${projectId}/package/links`,
      {},
      { command: 'package_links' },
    ),
  );
}

// ---------------------------------------------------------------------------
// hooks — each invalidates the project detail (artifacts hang off it)
// ---------------------------------------------------------------------------

/** Invalidate the project detail card so freshly (re)generated artifacts re-read. */
function useInvalidateProject(projectId: number): () => void {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
}

/** generate_documents mutation. Variables: optional generation_notes. */
export function useGenerateDocuments(
  projectId: number,
): UseMutationResult<GenerateDocumentsResponse, ApiResultError, string | undefined> {
  const invalidate = useInvalidateProject(projectId);
  return useMutation<GenerateDocumentsResponse, ApiResultError, string | undefined>({
    mutationFn: (notes) => generateDocumentsApi(projectId, notes),
    onSuccess: invalidate,
  });
}

/** generate_kickoff_sheet mutation. */
export function useGenerateKickoffSheet(
  projectId: number,
): UseMutationResult<GenerateKickoffSheetResponse, ApiResultError, string | undefined> {
  const invalidate = useInvalidateProject(projectId);
  return useMutation<GenerateKickoffSheetResponse, ApiResultError, string | undefined>({
    mutationFn: (notes) => generateKickoffSheetApi(projectId, notes),
    onSuccess: invalidate,
  });
}

/** generate_followup_email mutation. */
export function useGenerateFollowupEmail(
  projectId: number,
): UseMutationResult<GenerateFollowupEmailResponse, ApiResultError, string | undefined> {
  const invalidate = useInvalidateProject(projectId);
  return useMutation<GenerateFollowupEmailResponse, ApiResultError, string | undefined>({
    mutationFn: (notes) => generateFollowupEmailApi(projectId, notes),
    onSuccess: invalidate,
  });
}

/** Variables for the regenerate mutation. */
export interface RegenerateVars {
  readonly docType: RegeneratableDocType;
  readonly instruction: string;
  readonly generationNotes: string;
}

/** regenerate_document mutation (one core doc). */
export function useRegenerateDocument(
  projectId: number,
): UseMutationResult<RegenerateDocumentResponse, ApiResultError, RegenerateVars> {
  const invalidate = useInvalidateProject(projectId);
  return useMutation<RegenerateDocumentResponse, ApiResultError, RegenerateVars>({
    mutationFn: ({ docType, instruction, generationNotes }) =>
      regenerateDocumentApi(projectId, docType, instruction, generationNotes),
    onSuccess: invalidate,
  });
}

/** Variables for the save mutation. */
export interface SaveArtifactVars {
  readonly docType: EditableDocType;
  readonly content: Record<string, unknown>;
}

/** save_stage4_artifact mutation. */
export function useSaveArtifact(
  projectId: number,
): UseMutationResult<SaveArtifactResponse, ApiResultError, SaveArtifactVars> {
  const invalidate = useInvalidateProject(projectId);
  return useMutation<SaveArtifactResponse, ApiResultError, SaveArtifactVars>({
    mutationFn: ({ docType, content }) => saveArtifactApi(projectId, docType, content),
    onSuccess: invalidate,
  });
}

/** approve_artifact mutation. */
export function useApproveArtifact(
  projectId: number,
): UseMutationResult<ApproveArtifactResponse, ApiResultError, EditableDocType> {
  const invalidate = useInvalidateProject(projectId);
  return useMutation<ApproveArtifactResponse, ApiResultError, EditableDocType>({
    mutationFn: (docType) => approveArtifactApi(projectId, docType),
    onSuccess: invalidate,
  });
}

/**
 * confirm_package mutation (POST .../confirm-package). On success it invalidates
 * the project detail so the moved-to-Final stage + confirmed status re-read.
 */
export function useConfirmPackage(
  projectId: number,
): UseMutationResult<ConfirmPackageResponse, ApiResultError, void> {
  const invalidate = useInvalidateProject(projectId);
  return useMutation<ConfirmPackageResponse, ApiResultError, void>({
    mutationFn: () => confirmPackageApi(projectId),
    onSuccess: invalidate,
  });
}

/**
 * Shared key + fetcher for the notification defaults, so the hook below and an
 * imperative `ensureQueryData` cannot drift apart (KS3-03: the "Send to Sourcing
 * Team" click must be able to AWAIT the same cache entry the hook fills, instead
 * of silently composing without the template when the click wins the race).
 */
export function internalNotificationDefaultsQuery(projectId: number): {
  queryKey: readonly unknown[];
  queryFn: () => Promise<InternalNotificationDefaultsResponse>;
  staleTime: number;
} {
  return {
    queryKey: ['internal-notification-defaults', projectId],
    queryFn: () => internalNotificationDefaultsApi(projectId),
    staleTime: 60_000,
  };
}

/** internal_notification_defaults query (recipients + templates). */
export function useInternalNotificationDefaults(
  projectId: number,
  enabled: boolean = true,
): UseQueryResult<InternalNotificationDefaultsResponse, ApiResultError> {
  return useQuery<InternalNotificationDefaultsResponse, ApiResultError>({
    ...internalNotificationDefaultsQuery(projectId),
    enabled: enabled && projectId > 0,
  });
}

/** Stage-5 send mutation (internal_notification / comm items). */
export function useSendStage5(
  projectId: number,
): UseMutationResult<SendStage5Response, ApiResultError, SendStage5Request> {
  const invalidate = useInvalidateProject(projectId);
  return useMutation<SendStage5Response, ApiResultError, SendStage5Request>({
    mutationFn: (body) => sendStage5Api(projectId, body),
    onSuccess: invalidate,
  });
}

/**
 * package/links mutation (POST .../package/links) — generates fresh R2 links for
 * the Launched "Send Final Package" flow. A plain fire-and-return: it does NOT
 * mutate project state (it only renders + uploads the already-approved docs), so
 * there is NO invalidation. The caller composes the package email body from the
 * returned links; a rejection (e.g. 500 `R2_NOT_CONFIGURED`) is a typed
 * `ApiResultError` the caller surfaces via `errorMessage()`.
 */
export function useGeneratePackageLinks(
  projectId: number,
): UseMutationResult<PackageLinksResponse, ApiResultError, void> {
  return useMutation<PackageLinksResponse, ApiResultError, void>({
    mutationFn: () => packageLinksApi(projectId),
  });
}
